import { useEffect } from "react";
import ReactGA from "react-ga4";
import { FaApple } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import {
    Box, Button, Flex, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalOverlay, Text, useBreakpointValue, useColorModeValue,
} from "@chakra-ui/react";
import { appStoreUrl } from "../../constants";

const COPY = {
    save: {
        heading: "Save shops on Kuthé for iOS",
        body: "Your saved shops, map markers, and want-to-go list all live in the app.",
    },
    share: {
        heading: "Share shops from Kuthé for iOS",
        body: "Share any shop directly from the app - one tap, any platform.",
    },
    general: {
        heading: "Kuthé is now on iOS",
        body: "Find the best independent coffee shops near you. Save favourites, explore the map, discover what's nearby.",
    },
};

const AppModal = ({ isOpen, onClose, context = "general" }) => {
    const isDesktop = useBreakpointValue({ base: false, md: true });
    const bg        = useColorModeValue("#fdfaf5", "#120c06");
    const border    = useColorModeValue("rgba(139,62,18,0.12)", "rgba(200,169,126,0.08)");
    const text      = useColorModeValue("#1a0c04", "#e8d9c4");
    const dim       = useColorModeValue("#6a4020", "#a89880");
    const accent    = useColorModeValue("#8b3e12", "#c8a97e");
    const btnColor  = useColorModeValue("#fdfaf5", "#120c06");

    const { heading, body } = COPY[context] ?? COPY.general;
    const campaign = `app_modal_${context || "general"}`;
    const storeUrl = appStoreUrl(campaign);

    useEffect(() => {
        if (!isOpen) return;
        ReactGA.event({
            category: "App",
            action: "App Modal Open",
            label: context,
        });
    }, [isOpen, context]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
            <ModalOverlay bg="rgba(0,0,0,0.6)" backdropFilter="blur(4px)" />
            <ModalContent bg={bg} border="1px solid" borderColor={border} borderRadius="20px" mx={4} overflow="hidden">
                <ModalCloseButton color={dim} top={4} right={4} />
                <ModalBody px={7} pt={8} pb={7}>
                    <Flex direction="column" gap={5} align="center" textAlign="center">

                        <Box>
                            <Text fontFamily="heading" fontStyle="italic" fontSize="xl" fontWeight={400} color={text} lineHeight={1.2} mb={2}>
                                {heading}
                            </Text>
                            <Text fontSize="13px" color={dim} lineHeight={1.7} fontFamily="body">
                                {body}
                            </Text>
                        </Box>

                        <Flex direction="column" align="center" gap={4} w="full">
                            <Button
                                as="a"
                                href={storeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    ReactGA.event({
                                        category: "App",
                                        action: "App Store Click",
                                        label: `App Modal — ${context}`,
                                    });
                                }}
                                leftIcon={<FaApple size={16} />}
                                bg={accent}
                                color={btnColor}
                                borderRadius="full"
                                px={6}
                                py={5}
                                fontSize="13px"
                                fontFamily="body"
                                fontWeight={500}
                                letterSpacing="0.02em"
                                _hover={{ opacity: 0.88 }}
                                w="full"
                            >
                                Download on the App Store
                            </Button>

                            {isDesktop && (
                                <Flex direction="column" align="center" gap={1}>
                                    <Box p={2} bg="white" borderRadius="8px">
                                        <QRCodeSVG
                                            value={storeUrl}
                                            size={80}
                                            fgColor="#1a0c04"
                                            bgColor="#ffffff"
                                            level="M"
                                        />
                                    </Box>
                                    <Text fontSize="10px" color={dim} letterSpacing="0.06em" textTransform="uppercase" fontFamily="body">
                                        or scan to open
                                    </Text>
                                </Flex>
                            )}
                        </Flex>

                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AppModal;
